/**
 * The Assistant building dashboards: what it is told, and how its edits land.
 *
 * **What it is told.** Dashboards may exist only in this browser, and the open
 * one may hold unsaved edits, so the server cannot look them up - the page
 * describes them with each message (`buildDashboardContext`): the list by name,
 * and the open dashboard's tabs, charts, positions and recipes.
 *
 * **How edits land.** Exactly like a person's: through `editActiveDashboard`,
 * as unsaved changes the toolbar's Save keeps and Discard drops. Nothing here
 * writes a stored document except creating a new, empty dashboard, which the
 * New button does the same way. Positions are computed by `placement.ts` from
 * what the model asked for - it never supplies coordinates.
 *
 * `applyOperations` is the pure half and carries the rules; `applyDashboardEdit`
 * is the half that opens things, waits for them and takes a snapshot for Undo.
 */

import type {
  AssistantDashboardContext,
  AssistantDashboardTile,
  DashboardEdit,
  DashboardOperation,
  PlotSpec,
} from '../../api/types.ts';
import { stripContext } from '../../state/presets.ts';
import { activeSection, endDate, plottedShipIds, startDate } from '../../state/store.ts';
import { isPageVisible } from '../../state/page-visibility.ts';
import { shipLabel } from '../../utils/ships.ts';
import {
  DEFAULT_TILE_SOURCE,
  SLICER_SIZE,
  activeDashboardId,
  activeDashboardTabId,
  canEditDashboard,
  createDashboard,
  dashboardDirty,
  dashboardRoute,
  dashboards,
  editActiveDashboard,
  folders,
  followsVariables,
  genId,
  openDashboard,
  recipeFromChart,
  tilePinsContext,
  whenHydrated,
  type Dashboard,
  type DashboardTab,
  type DashboardTile,
  type LinkableChart,
  type LiveRefresh,
  type TileBind,
} from '../dashboard/dashboard-state.ts';
import { familyOf, shipColumns } from '../dashboard/slicer-state.ts';
import { ensureDashboardsLoaded } from '../dashboard/dashboard-repository.ts';
import {
  HEIGHT_ROWS,
  SIZE_COLUMNS,
  arrangeColumns,
  layoutRows,
  placeTile,
  resizeInPlace,
  type PlacementIntent,
} from '../dashboard/placement.ts';
import { resolveTile, tileSummary, suggestTileTitle } from '../dashboard/tile-spec.ts';
import {
  LIVE_WINDOW_VAR,
  SHIP_VAR,
  WINDOW_VAR,
  isRef,
  liveWindowVariable,
  modeFits,
  resolveVariables,
  storedLiveWindow,
  type SlicerFamily,
} from '../dashboard/variables.ts';
import { specToQuery } from './apply-plot.ts';
import { mergeRecipe, specFromQuery } from './spec-from-query.ts';
import { catalogCharts as availableCatalogCharts } from '../catalog/catalog-state.ts';
import { userMessage } from '../../api/errors.ts';

const MAX_LISTED = 300;
const MAX_TABS = 20;
const MAX_TILES_PER_TAB = 80;

/** Refresh Live charts in the model's words and in the document's. */
type RefreshWord = NonNullable<NonNullable<DashboardEdit['settings']>['live_refresh']>;
const REFRESH_WORDS: ReadonlyArray<[RefreshWord, LiveRefresh]> = [
  ['off', 'off'],
  ['as_written', 'cadence'],
  ['10s', 10],
  ['30s', 30],
  ['1m', 60],
  ['5m', 300],
];

/* ── what the model is told ─────────────────────────────── */

/**
 * The dashboards part of a chat request, or null when dashboards are not
 * available to this account at all - in which case the model is not offered
 * the tool either.
 */
export async function buildDashboardContext(): Promise<AssistantDashboardContext | null> {
  if (!isPageVisible('dashboards')) return null;
  try {
    await ensureDashboardsLoaded();
  } catch {
    return null;
  }
  const folderName = new Map(folders.value.map((f) => [f.id, f.name]));
  const list = dashboards.value.slice(0, MAX_LISTED).map((d) => ({
    id: d.id,
    name: d.name,
    folder: d.folderId ? (folderName.get(d.folderId) ?? null) : null,
    can_edit: canEditDashboard(d),
  }));

  const d = dashboards.value.find((x) => x.id === activeDashboardId.value);
  const open = d && dashboardRoute.value === 'view' && !d.partial ? describeOpen(d) : null;
  return { dashboards: list, open, can_create: true };
}

function describeOpen(d: Dashboard): NonNullable<AssistantDashboardContext['open']> {
  const shared = resolveVariables(d.variables, {
    ships: plottedShipIds(),
    start: startDate.value,
    end: endDate.value,
  });
  const detached = d.variables.some(
    (v) => (v.id === SHIP_VAR || v.id === WINDOW_VAR) && v.binding !== 'url',
  );
  return {
    id: d.id,
    name: d.name,
    can_edit: canEditDashboard(d),
    on_screen: activeSection.value === 'dashboards',
    unsaved: dashboardDirty.value,
    detached,
    ships: shared.ships.slice(0, 6),
    start: shared.start || null,
    end: shared.end || null,
    active_tab_id: activeDashboardTabId.value ?? d.tabs[0]?.id ?? null,
    live_window: storedLiveWindow(d.variables).preset ?? 'custom',
    live_refresh: REFRESH_WORDS.find(([, v]) => v === d.settings.liveRefresh)?.[0] ?? null,
    live_slicers: d.settings.liveSlicers,
    tabs: d.tabs.slice(0, MAX_TABS).map((tab) => ({
      id: tab.id,
      name: tab.name,
      tiles: tab.tiles.slice(0, MAX_TILES_PER_TAB).map((tile) => describeTile(tile, shared)),
    })),
  };
}

function describeTile(
  tile: DashboardTile,
  shared: ReturnType<typeof resolveVariables>,
): AssistantDashboardTile {
  if (tile.kind === 'slicer' && tile.slicer) {
    const { column, mode } = tile.slicer;
    return {
      id: tile.id,
      title: tile.title,
      summary: `slicer · ${column}`,
      pos: { ...tile.pos },
      follows_dashboard: true,
      linked: false,
      plot: null,
      kept_settings: [],
      kind: 'slicer',
      slicer: { column, mode },
    };
  }
  const resolved = resolveTile(tile, shared);
  const pinnedWindow = !isRef(tile.bind.window);
  const { spec, kept } = specFromQuery(
    tile.query,
    resolved.ships,
    pinnedWindow ? { start: resolved.start, end: resolved.end } : null,
  );
  return {
    id: tile.id,
    title: tile.title,
    summary: tileSummary(tile),
    pos: { ...tile.pos },
    follows_dashboard: !tilePinsContext(tile),
    linked: !!tile.chartId,
    plot: tile.chartId ? null : spec,
    kept_settings: kept,
  };
}

/* ── applying operations: the pure half ─────────────────── */

export interface ApplyOptions {
  /** The tab on screen; the default target. */
  activeTabId: string | null;
  /** The dashboard's "Float tiles up" setting. */
  float: boolean;
  /** Saved charts visible when the request was made, for linked-tile operations. */
  catalogCharts?: readonly LinkableChart[];
  /** Column families on the dashboard's main ship, for slicer operations. */
  columnFamilies?: ReadonlyMap<string, SlicerFamily>;
}

export interface ApplyResult {
  /** What changed, one line each, in words. */
  done: string[];
  /** What could not be done, and why. */
  problems: string[];
  /** The tab the last change happened on, to show it. */
  focusTabId: string | null;
}

const quote = (s: string): string => `“${s}”`;

/** A context-free recipe and a binding from a validated spec. */
export function recipeFromSpec(
  spec: PlotSpec,
  followDashboard: boolean,
): { query: string; bind: TileBind } {
  const query = stripContext(specToQuery(spec));
  // A Live chart keeps its own rolling window: the dashboard's window is days
  // or months, which Live does not hold. The ship still follows if asked.
  if (spec.source === 'live' && spec.live_window) {
    const ships = [spec.ship_id, ...(spec.compare_ship_ids ?? [])].filter(
      (s, i, all): s is string => !!s && all.indexOf(s) === i,
    );
    return {
      query,
      bind: { ship: followDashboard ? followsVariables().ship : ships, window: { preset: spec.live_window } },
    };
  }
  if (followDashboard) return { query, bind: followsVariables() };
  const ships = [spec.ship_id, ...(spec.compare_ship_ids ?? [])].filter(
    (s, i, all): s is string => !!s && all.indexOf(s) === i,
  );
  return {
    query,
    bind: {
      ship: ships,
      window:
        spec.start_date && spec.end_date
          ? { start: spec.start_date, end: spec.end_date }
          : `$${WINDOW_VAR}`,
    },
  };
}

/**
 * Apply operations to a dashboard document, in order, in place.
 *
 * An operation that cannot be carried out is reported and skipped; the rest
 * still apply. The server has already checked the batch against what the page
 * described, so a problem here means the dashboard changed in between - a tile
 * removed by hand while the answer was on its way - and saying so beats
 * refusing everything.
 */
export function applyOperations(
  d: Dashboard,
  ops: readonly DashboardOperation[],
  options: ApplyOptions,
): ApplyResult {
  const done: string[] = [];
  const problems: string[] = [];
  const tileKeys = new Map<string, string>();
  const tabKeys = new Map<string, string>();
  let focusTabId: string | null = null;

  const defaultTab = (): DashboardTab | undefined =>
    d.tabs.find((t) => t.id === options.activeTabId) ?? d.tabs[0];

  const findTab = (ref: string | null | undefined): DashboardTab | undefined => {
    if (!ref) return defaultTab();
    const id = tabKeys.get(ref) ?? ref;
    return (
      d.tabs.find((t) => t.id === id) ??
      d.tabs.find((t) => t.name.trim().toLowerCase() === ref.trim().toLowerCase())
    );
  };

  const findTile = (ref: string | null | undefined): { tab: DashboardTab; tile: DashboardTile } | null => {
    if (!ref) return null;
    const id = tileKeys.get(ref) ?? ref;
    for (const tab of d.tabs) {
      const tile = tab.tiles.find((t) => t.id === id);
      if (tile) return { tab, tile };
    }
    return null;
  };

  const titleOf = (id: string): string => {
    const hit = findTile(id);
    return hit ? quote(hit.tile.title) : id;
  };

  const intentOf = (op: DashboardOperation): PlacementIntent => ({
    beside: op.place?.beside ? (tileKeys.get(op.place.beside) ?? op.place.beside) : null,
    below: op.place?.below ? (tileKeys.get(op.place.below) ?? op.place.below) : null,
    newRow: !!op.place?.new_row,
  });

  const hasIntent = (op: DashboardOperation): boolean =>
    !!(op.place?.beside || op.place?.below || op.place?.new_row);

  /** Place `tile` (already in or out of `tab.tiles`) by intent, and commit positions. */
  const place = (tab: DashboardTab, tile: DashboardTile, w: number, h: number, intent: PlacementIntent): string | undefined => {
    const others = tab.tiles.filter((t) => t.id !== tile.id);
    const result = placeTile(others, tile.id, { w, h }, intent, options.float, titleOf);
    if (!tab.tiles.includes(tile)) tab.tiles.push(tile);
    commit(tab, result.positions);
    return result.note;
  };

  const commit = (tab: DashboardTab, positions: Array<{ id: string; pos: DashboardTile['pos'] }>): void => {
    const byId = new Map(positions.map((p) => [p.id, p.pos]));
    for (const t of tab.tiles) {
      const pos = byId.get(t.id);
      if (pos) t.pos = { ...pos };
    }
  };

  const widthWord = (w: number): string =>
    w >= 12 ? 'full width' : w === 6 ? 'half width' : `${w} of 12 columns wide`;

  for (const [i, op] of ops.entries()) {
    const fail = (why: string): void => {
      problems.push(`Step ${i + 1} (${op.op.replace('_', ' ')}): ${why}`);
    };
    try {
      switch (op.op) {
        case 'add_tab': {
          const name = op.title?.trim() || `Tab ${d.tabs.length + 1}`;
          const tab: DashboardTab = { id: genId('t'), name, variables: [], rows: [], tiles: [] };
          d.tabs.push(tab);
          if (op.key) tabKeys.set(op.key, tab.id);
          done.push(`Added the tab ${quote(name)}`);
          focusTabId = tab.id;
          break;
        }
        case 'rename_tab': {
          const tab = findTab(op.tab);
          if (!tab || !op.title?.trim()) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          const before = tab.name;
          tab.name = op.title.trim();
          done.push(`Renamed the tab ${quote(before)} to ${quote(tab.name)}`);
          focusTabId = tab.id;
          break;
        }
        case 'arrange_tab': {
          const tab = findTab(op.tab);
          if (!tab) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          const columns = op.columns ?? 2;
          commit(tab, arrangeColumns(tab.tiles, columns));
          done.push(`Arranged ${quote(tab.name)} into ${columns} column${columns === 1 ? '' : 's'}`);
          focusTabId = tab.id;
          break;
        }
        case 'layout_tab': {
          const tab = findTab(op.tab);
          if (!tab) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          const rows = (op.rows ?? []).map((row) => {
            const ids: string[] = [];
            for (const ref of row.charts) {
              const hit = findTile(ref);
              if (!hit) fail(`${quote(ref)} is no longer on the dashboard`);
              else if (hit.tab !== tab) fail(`${quote(hit.tile.title)} is on another tab`);
              else ids.push(hit.tile.id);
            }
            // Sizes stay aligned with the charts that were found.
            const sizes = row.sizes && row.sizes.length === row.charts.length
              ? row.charts.flatMap((ref, j) => {
                  const hit = findTile(ref);
                  return hit && hit.tab === tab ? [row.sizes![j]!] : [];
                })
              : null;
            return { ids, sizes, height: row.height ?? null };
          });
          commit(tab, layoutRows(tab.tiles, rows));
          const count = rows.filter((r) => r.ids.length).length;
          done.push(`Laid out ${quote(tab.name)} in ${count} row${count === 1 ? '' : 's'}`);
          focusTabId = tab.id;
          break;
        }
        case 'add_chart': {
          const tab = findTab(op.tab);
          if (!tab) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          if (!op.plot) { fail('no chart was given'); break; }
          const { query, bind } = recipeFromSpec(op.plot, op.follow_dashboard !== false);
          const w = SIZE_COLUMNS[op.size ?? 'third'];
          const h = HEIGHT_ROWS[op.height ?? 'normal'];
          const tile: DashboardTile = {
            id: genId('c'),
            rowId: null,
            kind: 'chart',
            title: op.title?.trim() || suggestTileTitle(query),
            query,
            source: op.plot.source ?? DEFAULT_TILE_SOURCE,
            bind,
            filters: [],
            repeat: null,
            chartId: null,
            pos: { x: 0, y: 0, w, h },
          };
          if (op.key) tileKeys.set(op.key, tile.id);
          const note = place(tab, tile, w, h, intentOf(op));
          const pinned = op.follow_dashboard === false ? `, fixed to ${shipLabel(op.plot.ship_id)}` : '';
          done.push(
            `Added ${quote(tile.title)} to ${quote(tab.name)} (${widthWord(tile.pos.w)}${pinned})` +
              (note ? ` - ${note}` : ''),
          );
          focusTabId = tab.id;
          break;
        }
        case 'add_catalog_charts': {
          const tab = findTab(op.tab);
          if (!tab) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          const byId = new Map((options.catalogCharts ?? []).map((chart) => [chart.id, chart]));
          const ids = op.catalog_chart_ids ?? [];
          if (!ids.length) { fail('no Catalog charts were given'); break; }
          const w = SIZE_COLUMNS[op.size ?? 'third'];
          const h = HEIGHT_ROWS[op.height ?? 'normal'];
          const added: string[] = [];
          for (const [chartIndex, id] of ids.entries()) {
            const chart = byId.get(id);
            if (!chart) {
              fail(`Catalog chart ${quote(id)} is no longer available`);
              continue;
            }
            const tile: DashboardTile = {
              id: genId('c'),
              rowId: null,
              kind: 'chart',
              filters: [],
              repeat: null,
              ...recipeFromChart(chart),
              chartId: chart.id,
              pos: { x: 0, y: 0, w, h },
            };
            const intent = chartIndex === 0 ? intentOf(op) : {};
            place(tab, tile, w, h, intent);
            added.push(tile.title);
          }
          if (added.length === 1) done.push(`Linked ${quote(added[0]!)} from the Catalog to ${quote(tab.name)}`);
          else if (added.length > 1) done.push(`Linked ${added.length} Catalog charts to ${quote(tab.name)}`);
          focusTabId = tab.id;
          break;
        }
        case 'add_slicer':
        case 'update_slicer': {
          const column = op.slicer?.column ?? '';
          const family = options.columnFamilies?.get(column);
          if (!op.slicer || !family) { fail(`${quote(column)} is not a column on the dashboard's ship`); break; }
          if (!modeFits(op.slicer.mode, family)) { fail(`${quote(column)} cannot be shown as ${op.slicer.mode}`); break; }
          const slicer = { column, family, mode: op.slicer.mode };
          if (op.op === 'update_slicer') {
            const hit = findTile(op.tile);
            if (!hit?.tile.slicer) { fail('that slicer is no longer on the dashboard'); break; }
            const wasList = hit.tile.slicer.mode === 'list';
            hit.tile.slicer = slicer;
            hit.tile.title = op.title?.trim() || hit.tile.title;
            // Same rule as updateSlicerTile: a list needs its own height.
            if (wasList !== (slicer.mode === 'list')) {
              commit(hit.tab, resizeInPlace(hit.tab.tiles, hit.tile.id, { w: hit.tile.pos.w, h: SLICER_SIZE[slicer.mode][1] }, options.float));
            }
            done.push(`Changed the slicer ${quote(hit.tile.title)} to ${slicer.mode} on ${column}`);
            focusTabId = hit.tab.id;
            break;
          }
          const tab = findTab(op.tab);
          if (!tab) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          const w = op.size ? SIZE_COLUMNS[op.size] : SLICER_SIZE[slicer.mode][0];
          // The mode decides the height: a one-line slicer six rows tall is mostly empty.
          const h = SLICER_SIZE[slicer.mode][1];
          const tile: DashboardTile = {
            id: genId('f'),
            rowId: null,
            kind: 'slicer',
            title: op.title?.trim() || column,
            query: '',
            source: DEFAULT_TILE_SOURCE,
            bind: followsVariables(),
            filters: [],
            repeat: null,
            chartId: null,
            slicer,
            pos: { x: 0, y: 0, w, h },
          };
          if (op.key) tileKeys.set(op.key, tile.id);
          const note = place(tab, tile, w, h, intentOf(op));
          done.push(`Added the slicer ${quote(tile.title)} (${slicer.mode} on ${column}) to ${quote(tab.name)}` + (note ? ` - ${note}` : ''));
          focusTabId = tab.id;
          break;
        }
        case 'update_chart': {
          const hit = findTile(op.tile);
          if (!hit) { fail(`that chart is no longer on the dashboard`); break; }
          if (hit.tile.kind === 'slicer') { fail(`${quote(hit.tile.title)} is a slicer`); break; }
          if (hit.tile.chartId) { fail(`${quote(hit.tile.title)} is linked to the Catalog`); break; }
          if (!op.plot) { fail('no chart was given'); break; }
          const { query, bind } = recipeFromSpec(op.plot, op.follow_dashboard !== false);
          hit.tile.query = mergeRecipe(hit.tile.query, query);
          hit.tile.bind = bind;
          if (op.title?.trim()) hit.tile.title = op.title.trim();
          done.push(`Updated ${quote(hit.tile.title)}`);
          if (op.size || op.height) {
            const w = op.size ? SIZE_COLUMNS[op.size] : hit.tile.pos.w;
            const h = op.height ? HEIGHT_ROWS[op.height] : hit.tile.pos.h;
            commit(hit.tab, resizeInPlace(hit.tab.tiles, hit.tile.id, { w, h }, options.float));
          }
          focusTabId = hit.tab.id;
          break;
        }
        case 'rename_chart': {
          const hit = findTile(op.tile);
          if (!hit || !op.title?.trim()) { fail('that chart is no longer on the dashboard'); break; }
          const before = hit.tile.title;
          hit.tile.title = op.title.trim();
          done.push(`Renamed ${quote(before)} to ${quote(hit.tile.title)}`);
          focusTabId = hit.tab.id;
          break;
        }
        case 'remove_chart': {
          const hit = findTile(op.tile);
          if (!hit) { fail('that chart is no longer on the dashboard'); break; }
          hit.tab.tiles = hit.tab.tiles.filter((t) => t.id !== hit.tile.id);
          if (options.float) commit(hit.tab, compactPositions(hit.tab.tiles));
          done.push(`Removed ${quote(hit.tile.title)}`);
          focusTabId = hit.tab.id;
          break;
        }
        case 'move_chart': {
          const hit = findTile(op.tile);
          if (!hit) { fail('that chart is no longer on the dashboard'); break; }
          const { tile } = hit;
          let tab = hit.tab;
          const w = op.size ? SIZE_COLUMNS[op.size] : tile.pos.w;
          const h = op.height ? HEIGHT_ROWS[op.height] : tile.pos.h;
          const target = op.tab ? findTab(op.tab) : tab;
          if (!target) { fail(`there is no tab ${quote(op.tab ?? '')}`); break; }
          if (target !== tab) {
            tab.tiles = tab.tiles.filter((t) => t.id !== tile.id);
            if (options.float) commit(tab, compactPositions(tab.tiles));
            tab = target;
          }
          let note: string | undefined;
          if (hasIntent(op) || !tab.tiles.includes(tile)) {
            // Taken out first, so a gap it leaves can close before it is put back.
            tab.tiles = tab.tiles.filter((t) => t.id !== tile.id);
            if (options.float) commit(tab, compactPositions(tab.tiles));
            note = place(tab, tile, w, h, intentOf(op));
          } else {
            commit(tab, resizeInPlace(tab.tiles, tile.id, { w, h }, options.float));
          }
          const where = target !== hit.tab ? ` to ${quote(tab.name)}` : '';
          done.push(`Moved ${quote(tile.title)}${where} (${widthWord(tile.pos.w)})` + (note ? ` - ${note}` : ''));
          focusTabId = tab.id;
          break;
        }
        default:
          fail('not something the dashboard can do');
      }
    } catch (e) {
      fail(userMessage(e));
    }
  }

  return { done, problems, focusTabId };
}

function compactPositions(tiles: DashboardTile[]): Array<{ id: string; pos: DashboardTile['pos'] }> {
  // Resizing a tile to its own size is a no-op placement that floats the tab.
  const first = tiles[0];
  if (!first) return [];
  return resizeInPlace(tiles, first.id, { w: first.pos.w, h: first.pos.h }, true);
}

/* ── applying an edit: the effectful half ───────────────── */

export interface DashboardEditOutcome {
  ok: boolean;
  dashboardId: string | null;
  dashboardName: string | null;
  created: boolean;
  done: string[];
  problems: string[];
  /** Puts the dashboard back as it was before this edit; false when it cannot. */
  undo: (() => boolean) | null;
}

/** One line for the model's next turn: what actually happened. */
export function outcomeNote(outcome: DashboardEditOutcome): string {
  const parts: string[] = [];
  if (outcome.dashboardName) {
    parts.push(`${outcome.created ? 'created and opened' : 'opened'} "${outcome.dashboardName}" (${outcome.dashboardId})`);
  }
  if (outcome.done.length) parts.push('done: ' + outcome.done.join('; '));
  if (outcome.problems.length) parts.push('NOT done: ' + outcome.problems.join('; '));
  return parts.join('. ').slice(0, 1990);
}

type Navigate = () => void;

/**
 * Open (or create) the dashboard and apply the edit as unsaved changes.
 *
 * `navigate` shows the Dashboards section through the shell's own navigation,
 * so the conversation stays beside it exactly as it does for any other move.
 */
export async function applyDashboardEdit(
  edit: DashboardEdit,
  navigate: Navigate,
): Promise<DashboardEditOutcome> {
  const outcome: DashboardEditOutcome = {
    ok: false,
    dashboardId: null,
    dashboardName: null,
    created: false,
    done: [],
    problems: [],
    undo: null,
  };
  const stop = (why: string): DashboardEditOutcome => {
    outcome.problems.push(why);
    return outcome;
  };

  if (!isPageVisible('dashboards')) return stop('Dashboards are not available for this account.');
  await ensureDashboardsLoaded();

  const currentId = dashboardRoute.value === 'view' ? activeDashboardId.value : null;
  const name = edit.new_dashboard_name?.trim();
  const wantedId = name ? null : (edit.dashboard_id ?? currentId);

  // Switching away from a dashboard with unsaved edits would strand them: the
  // dirty flag is per open dashboard. A person gets asked; the Assistant stops.
  if (dashboardDirty.value && currentId && (name || wantedId !== currentId)) {
    const open = dashboards.value.find((x) => x.id === currentId);
    return stop(
      `${quote(open?.name ?? 'The open dashboard')} has unsaved changes. Save or discard them first, then ask again.`,
    );
  }

  let d: Dashboard | undefined;
  if (name) {
    const made = await createDashboard(name);
    if (!made) return stop(`Could not create ${quote(name)}.`);
    d = made;
    outcome.created = true;
  } else {
    if (!wantedId) return stop('No dashboard is open.');
    d = dashboards.value.find((x) => x.id === wantedId);
    if (!d) return stop('That dashboard is no longer in your list.');
  }

  outcome.dashboardId = d.id;
  outcome.dashboardName = d.name;
  navigate();
  if (activeDashboardId.value !== d.id || dashboardRoute.value !== 'view') openDashboard(d.id);
  if (!(await whenHydrated(d.id))) return stop(`${quote(d.name)} did not finish loading.`);
  // Hydration replaces the row with the fetched document.
  d = dashboards.value.find((x) => x.id === outcome.dashboardId)!;

  const ops = edit.operations ?? [];
  if (!ops.length && !edit.selection && !edit.settings) {
    outcome.ok = true;
    return outcome;
  }
  if (!canEditDashboard(d)) return stop(`${quote(d.name)} is view only for you.`);

  const snapshot = JSON.stringify({ name: d.name, variables: d.variables, tabs: d.tabs, settings: d.settings });
  const dirtyBefore = dashboardDirty.value;
  const tabBefore = activeDashboardTabId.value;
  const doc = d;

  if (edit.selection) {
    const sel = edit.selection;
    editActiveDashboard((dash) => {
      const ships = (sel.ship_ids ?? []).filter(Boolean);
      const shipVar = dash.variables.find((v) => v.id === SHIP_VAR);
      if (ships.length && shipVar) shipVar.current = ships;
      const windowVar = dash.variables.find((v) => v.id === WINDOW_VAR);
      if (sel.start_date && sel.end_date && windowVar) {
        windowVar.current = { start: sel.start_date, end: sel.end_date };
      }
      if (sel.live_window) {
        // A dashboard saved before the Live window existed gets it on first use.
        if (!dash.variables.some((v) => v.id === LIVE_WINDOW_VAR)) dash.variables.push(liveWindowVariable());
        dash.variables.find((v) => v.id === LIVE_WINDOW_VAR)!.current = { preset: sel.live_window };
      }
    });
    const bits: string[] = [];
    if (sel.ship_ids?.length) bits.push(sel.ship_ids.map(shipLabel).join(', '));
    if (sel.start_date && sel.end_date) bits.push(`${sel.start_date.slice(0, 10)} to ${sel.end_date.slice(0, 10)}`);
    if (bits.length) outcome.done.push(`Set the dashboard to ${bits.join(', ')}, separate from Explore`);
    if (sel.live_window) outcome.done.push(`Set the Live time picker to ${sel.live_window}`);
  }

  if (edit.settings) {
    const { live_refresh, live_slicers } = edit.settings;
    const refresh = REFRESH_WORDS.find(([word]) => word === live_refresh)?.[1];
    editActiveDashboard((dash) => {
      if (refresh !== undefined) dash.settings.liveRefresh = refresh;
      if (live_slicers != null) dash.settings.liveSlicers = live_slicers;
    });
    if (refresh !== undefined) outcome.done.push(`Set Refresh Live charts to ${live_refresh}`);
    if (live_slicers != null) outcome.done.push(`Turned Filter while dragging ${live_slicers ? 'on' : 'off'}`);
  }

  // Slicer operations need the column's kind, which only the ship's columns say.
  const columnFamilies = new Map<string, SlicerFamily>();
  const ship =
    edit.selection?.ship_ids?.[0] ??
    resolveVariables(d.variables, { ships: plottedShipIds(), start: startDate.value, end: endDate.value }).ships[0];
  if (ship && ops.some((op) => op.slicer)) {
    for (const c of await shipColumns(ship).catch(() => [])) columnFamilies.set(c.name, familyOf(c.type));
  }

  let focus: string | null = null;
  if (ops.length) {
    editActiveDashboard((dash) => {
      const result = applyOperations(dash, ops, {
        activeTabId: activeDashboardTabId.value,
        float: dash.settings.floatTiles,
        catalogCharts: availableCatalogCharts.value,
        columnFamilies,
      });
      outcome.done.push(...result.done);
      outcome.problems.push(...result.problems);
      focus = result.focusTabId;
    });
  }
  if (focus) activeDashboardTabId.set(focus);

  outcome.ok = outcome.done.length > 0;
  outcome.undo = () => {
    const current = dashboards.value.find((x) => x.id === doc.id);
    if (!current || activeDashboardId.value !== doc.id) return false;
    const saved = dashboardDirty.value === false;
    Object.assign(current, JSON.parse(snapshot));
    current.updatedAt = new Date().toISOString();
    dashboards.notify();
    // Saved since: the stored copy has the edit, so going back is itself a change.
    dashboardDirty.set(saved ? true : dirtyBefore);
    if (tabBefore && current.tabs.some((t) => t.id === tabBefore)) activeDashboardTabId.set(tabBefore);
    return true;
  };
  return outcome;
}
